import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

// Hooks Customizados
import { useDashboardData } from './hooks/useDashboardData';
import { useSync } from './hooks/useSync';
import { useExport } from './hooks/useExport';

// Componentes Comuns
import LoadingScreen from '../../components/common/LoadingScreen';
import SupportWidget from '../../components/common/SupportWidget';
import { Button } from '../../components/common/Button';

// Componentes UI da Página
import KeywordsAnalysis from './components/KeywordsAnalysis';
import KeywordsManager from './components/KeywordsManager';
import DashboardHeader from './components/DashboardHeader';
import SyncReport from './components/SyncReport';
import FiltersSection from './components/FiltersSection';
import StatsCards from './components/StatsCards';
import ChartsSection from './components/ChartsSection';
import CommentsTable from './components/CommentsTable';
import HistoryModal from './components/HistoryModal';

// Estilos
import { DashboardContainer } from './styles';

function HomeCliente({ user, perfil, onLogout }) {
    const navigate = useNavigate();
    
    // Chave única para salvar os filtros deste usuário específico
    const STORAGE_KEY = `dash_filters_${user?.cliente_id}`;
    const userHistoryLimit = perfil?.plan_history_days || 30;
    const keywordsLimit = perfil?.plan_keywords_limit || 10; 

    // 1. Inicialização do Estado (Lazy Initialization)
    // Tenta ler do localStorage. Se não existir, usa o padrão.
    const [filtros, setFiltros] = useState(() => {
        try {
            const savedFilters = localStorage.getItem(STORAGE_KEY);
            return savedFilters ? JSON.parse(savedFilters) : {
                pagina: 'todas',
                tipo: 'todos',
                post: 'todos',
                viewInicio: '',
                viewFim: ''
            };
        } catch (error) {
            console.error("Erro ao ler filtros:", error);
            return {
                pagina: 'todas',
                tipo: 'todos',
                post: 'todos',
                viewInicio: '',
                viewFim: ''
            };
        }
    });

    // 2. Persistência
    // Toda vez que 'filtros' mudar, salvamos no localStorage
    useEffect(() => {
        if (user?.cliente_id) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtros));
        }
    }, [filtros, STORAGE_KEY, user?.cliente_id]);
    
    const [filtroSentimento, setFiltroSentimento] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    
    // Estado para alternar entre Análise e Configuração de Keywords
    const [showKeywordsConfig, setShowKeywordsConfig] = useState(false);
    const [keywordsData, setKeywordsData] = useState([]); 

    // 3. Data Hook (Continua igual)
    const { 
        pages, posts, comentarios, loading, pageMetrics, mediaTypes, 
        aggregatedMetrics, postsFiltradosGeral, fetchData 
    } = useDashboardData(user, filtros);

    // 4. Sync Hook
    const handleSyncSuccess = () => {
        fetchData();
    };

    const { isSyncing, syncReport, syncDisabled, timeLeft, executeSync } = useSync(user, handleSyncSuccess);

    // Função wrapper para o sync confirmar e setar filtros de data se necessário
    const handleSyncConfirm = (start, end) => {
        executeSync(start, end, () => {
            setShowHistoryModal(false);

            if (start && end) {
                // Ao setar novos filtros aqui, o useEffect acima já vai salvar automaticamente
                setFiltros(prev => ({
                    ...prev,
                    viewInicio: start,
                    viewFim: end
                }));
            }
        });
    };

    // 5. Preparação para Exportação
    const comentariosParaExport = comentarios.filter(c => 
        postsFiltradosGeral.some(p => p.post_id === c.post)
    );

    // 6. Export Hook
    const { showExportMenu, setShowExportMenu, exportRef, handleExport } = useExport(
        postsFiltradosGeral, 
        comentariosParaExport, 
        aggregatedMetrics, 
        pageMetrics,
        pages,
        keywordsData
    );

    if (loading) {
        return <LoadingScreen message="Carregando Dashboard..." />;
    }

    return (
        <DashboardContainer>
            
            <DashboardHeader 
                user={user} 
                perfil={perfil} 
                onLogout={onLogout}
                syncProps={{ isSyncing, syncDisabled, timeLeft, executeSync: () => handleSyncConfirm() }}
                exportProps={{ 
                    showExportMenu, 
                    setShowExportMenu, 
                    handleExport: (type) => handleExport(type), 
                    exportRef 
                }}
                onOpenHistory={() => setShowHistoryModal(true)}
            />

            <SyncReport report={syncReport} />

            <FiltersSection 
                pages={pages} 
                posts={postsFiltradosGeral} 
                mediaTypes={mediaTypes}
                filtros={filtros} 
                setFiltros={setFiltros} 
            />

            <StatsCards 
                metrics={aggregatedMetrics} 
                pageMetrics={pageMetrics}
                filtros={filtros}
                navigate={navigate}
            />

            <ChartsSection 
                posts={postsFiltradosGeral} 
                comentarios={comentarios}
                filtros={filtros}
                filtroSentimento={filtroSentimento}
                setFiltroSentimento={setFiltroSentimento}
            />

            {/* SEÇÃO DE INTELIGÊNCIA DE TÓPICOS */}
            <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                {showKeywordsConfig ? (
                    <div style={{position: 'relative'}}>
                        <KeywordsManager user={user} limit={keywordsLimit} />
                        <Button 
                            variant="secondary" 
                            onClick={() => setShowKeywordsConfig(false)}
                            style={{marginTop: '1rem'}}
                        >
                            Voltar para Gráfico
                        </Button>
                    </div>
                ) : (
                    // Visão de Análise (Gráfico de Barras)
                    <KeywordsAnalysis 
                        user={user} 
                        filtros={filtros}
                        onManageClick={() => setShowKeywordsConfig(true)}
                        onDataLoaded={setKeywordsData} 
                    />
                )}
            </div>

            <CommentsTable 
                comentarios={comentarios} 
                posts={posts} 
                pages={pages} // Mantive a alteração anterior
                filtros={filtros}
                filtroSentimento={filtroSentimento}
                setFiltroSentimento={setFiltroSentimento}
            />

            {showHistoryModal && (
                <HistoryModal 
                    onClose={() => setShowHistoryModal(false)} 
                    onConfirm={handleSyncConfirm} 
                    limitDays={userHistoryLimit}
                />
            )}
            
            <SupportWidget user={user} />
        </DashboardContainer>
    );
}

export default HomeCliente;